<template>
	<v-card style="height: 100%; width: 100%">
		<v-card-title :class="$vuetify.breakpoint.mdAndDown ? 'py-2' : ''" class="pb-3">
			<v-row no-gutters>
				<v-col cols="6" class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
					{{ $t('statuses') }}
				</v-col>
			</v-row>
		</v-card-title>
		<v-card-text
			class="overflow-auto pt-1"
			:style="$vuetify.breakpoint.mdAndDown ? 'max-height: calc(100% - 48px)' : 'max-height: calc(100% - 64px)'">
			<v-expansion-panels style="height: calc(100% - 56px)">
				<v-expansion-panel>
					<v-expansion-panel-header class="pa-2">
						<template v-slot:actions>
							<v-icon color="primary">mdi-menu-down</v-icon>
						</template>
						<v-row no-gutters>
							<v-col cols="10" class="text-left text-body-1">
								{{ $t('functional-status-header') }}
							</v-col>
							<v-col cols="2" class="text-right">
								<v-icon
									color="primary"
									:size="$vuetify.breakpoint.mdAndDown ? 20 : 24"
									@click.stop="synchronizeEditDrawers('ccmFunctionalStatusEdit')">
									mdi-pencil
								</v-icon>
							</v-col>
						</v-row>
					</v-expansion-panel-header>
					<v-expansion-panel-content class="low-padding-expansion-panel">
						<v-expansion-panels>
							<v-expansion-panel v-for="panel in functionalStatusData" :key="panel.category">
								<v-expansion-panel-header class="pa-2">
									<template v-slot:actions>
										<v-icon color="primary">mdi-menu-down</v-icon>
									</template>

									<v-row no-gutters>
										<v-col class="text-left text-body-1">
											<v-row v-if="panel.category === 'ADLs' || panel.category === 'IADLs'" dense class="ma-0">
												<v-col cols="5" class="pa-0">
													{{ panel.name }}
												</v-col>
												<v-col cols="7" class="pa-0 text-right">
													<v-chip v-if="panel.independent" small color="green lighten-5">
														<v-icon small class="mr-1">mdi-check-circle</v-icon>
														{{ $t('independent') }}
													</v-chip>
													<v-chip v-else small class="mr-1" color="red lighten-5">
														<v-icon small class="mr-1">mdi-alert</v-icon>
														{{ $t('dependent') }}
													</v-chip>
												</v-col>
											</v-row>
											<v-row v-else>
												<v-col>
													{{ panel.name }}
												</v-col>
											</v-row>
										</v-col>
									</v-row>
								</v-expansion-panel-header>
								<v-expansion-panel-content class="no-padding-expansion-panel">
									<v-divider class="my-1" />
									<v-row no-gutters>
										<v-col
											cols="12"
											v-for="item in panel.items"
											:key="item.id"
											class="text-left pa-2 d-flex align-center">
											<v-icon v-if="item.flag" color="primary">mdi-checkbox-blank</v-icon>
											<v-icon v-else color="primary"> mdi-checkbox-blank-outline</v-icon>
											<span class="ml-2">
												{{ item.value }}
											</span>
										</v-col>
									</v-row>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</v-expansion-panel-content>
				</v-expansion-panel>

				<v-expansion-panel>
					<v-expansion-panel-header class="pa-2">
						<template v-slot:actions>
							<v-icon color="primary"> mdi-menu-down </v-icon>
						</template>

						<v-row no-gutters>
							<v-col cols="10" class="text-left text-body-1">
								{{ $t('complex-decision-making-header') }}
							</v-col>
							<v-col cols="2" class="text-right">
								<v-icon
									color="primary"
									:size="$vuetify.breakpoint.mdAndDown ? 20 : 24"
									@click.stop="synchronizeEditDrawers('ccmComplexDecisionMakingEdit')">
									mdi-pencil
								</v-icon>
							</v-col>
						</v-row>
					</v-expansion-panel-header>

					<v-expansion-panel-content>
						<v-divider class="my-1" />
						<v-row id="complexDecisionMakingSection" no-gutters>
							<v-col cols="12">
								<v-row no-gutters>
									<v-col
										cols="12"
										v-for="item in complexDecisionMakingData"
										:key="item.id"
										class="text-left px-0 py-2 d-flex align-center">
										<v-icon v-if="item.flag" color="primary">mdi-checkbox-blank</v-icon>
										<v-icon v-else color="primary"> mdi-checkbox-blank-outline</v-icon>
										<span class="ml-2">
											{{ item.value }}
										</span>
									</v-col>
								</v-row>
							</v-col>
						</v-row>
					</v-expansion-panel-content>
				</v-expansion-panel>

				<v-expansion-panel>
					<v-expansion-panel-header class="pa-2">
						<template v-slot:actions>
							<v-icon color="primary"> mdi-menu-down </v-icon>
						</template>
						<v-row no-gutters>
							<v-col cols="10" class="text-left text-body-1">
								{{ $t('social-determinants-header') }}
							</v-col>
							<v-col cols="2" class="text-right">
								<v-icon
									color="primary"
									:size="$vuetify.breakpoint.mdAndDown ? 20 : 24"
									@click.stop="synchronizeEditDrawers('ccmSocialDeterminantsEdit')">
									mdi-pencil
								</v-icon>
							</v-col>
						</v-row>
					</v-expansion-panel-header>

					<v-expansion-panel-content>
						<v-divider class="my-1" />
						<v-row id="socialDeterminantsSection" no-gutters>
							<v-col cols="12">
								<v-row no-gutters>
									<v-col
										cols="12"
										v-for="item in socialDeterminantsData"
										:key="item.id"
										class="text-left px-0 py-2 d-flex align-center">
										<v-icon v-if="item.flag" color="primary">mdi-checkbox-blank</v-icon>
										<v-icon v-else color="primary"> mdi-checkbox-blank-outline</v-icon>
										<span class="ml-2">
											{{ item.value }}
										</span>
									</v-col>
								</v-row>
							</v-col>
						</v-row>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>
		</v-card-text>
	</v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
	name: 'FunctionalStatus',
	data () {
		return {
			functionalStatusData: null,
			complexDecisionMakingData: null,
			socialDeterminantsData: null,
		};
	},

	computed: {
		...mapState({
			currentRecord: (state) => state.ccmRecords.ccmRecord,
		}),
	},

	methods: {
		...mapActions({
			getFunctionalStatusTemplate: 'ccmRecords/getFunctionalStatusTemplate',
			getComplexDecisionMakingTemplate: 'ccmRecords/getComplexDecisionMakingTemplate',
			getSocialDeterminantsTemplate: 'ccmRecords/getSocialDeterminantsTemplate',
		}),
		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
		async getFunctionalStatusData () {
			let rawData;
			try {
				rawData = JSON.parse(this.currentRecord.functionalStatus);
			} catch (e) {
				rawData = null;
			}
			if (!rawData) {
				const response = await this.getFunctionalStatusTemplate();
				rawData = response.data;
				rawData[0].independent = true;
				rawData[0].dependentOnAll = false;
				rawData[1].independent = true;
				rawData[1].dependentOnAll = false;
			}
			return rawData;
		},
		async getComplexDecisionMakingData () {
			let rawData;
			try {
				rawData = JSON.parse(this.currentRecord.complexDecisionMaking);
			} catch (e) {
				rawData = null;
			}
			if (!rawData) {
				const response = await this.getComplexDecisionMakingTemplate();
				return response.data;
			}
			return rawData;
		},
		async getSocialDeterminantsData () {
			let rawData;
			try {
				rawData = JSON.parse(this.currentRecord.socialDeterminants);
			} catch (e) {
				rawData = null;
			}
			if (!rawData) {
				const response = await this.getSocialDeterminantsTemplate();
				return response.data;
			}
			return rawData;
		},
	},

	watch: {
		currentRecord: {
			async handler () {
				this.functionalStatusData = await this.getFunctionalStatusData();
				this.complexDecisionMakingData = await this.getComplexDecisionMakingData();
				this.socialDeterminantsData = await this.getSocialDeterminantsData();
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>
